"use client"

/**
 * The one scope (school > program / brand > site > location) picker menu.
 *
 * This body was previously copy-pasted into `UtilityBarSchoolSwitcher` and the
 * sidebar's `TeamSwitcher`, each with its own `useState` — so the two chrome
 * surfaces could show different programs at the same time. Both now render
 * this component over shared `useActiveScope` state, which the products home
 * writes to before the user has even entered the product.
 *
 * Only the *trigger* differs per surface: an avatar circle in the utility bar,
 * a full row in the sidebar, a card row on the products home. The menu itself
 * is identical everywhere, so it lives here once.
 *
 * Whether there is a menu at all is also decided here, once, as `fixed`: a
 * session that does not choose its scope (a student, who is enrolled in one
 * program at one school) gets the names and no trigger.
 */

import * as React from "react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { FILTER_LIST_SEARCH_THRESHOLD } from "@exxatdesignux/ui/lib/filter-list-search-threshold"
import type { Product } from "@/contexts/product-context"
import { recentProgramIds, useActiveScope, type ScopeStatus } from "@/hooks/use-active-scope"
import { useLoginSession } from "@/hooks/use-login-session"
import {
  canChangeSchool as sessionMayChangeSchool,
  resolveScopeConfigForSession,
  sessionMenuParent,
} from "@/lib/program-personas"
import type { ScopeChild, ScopeConfig, ScopeParent } from "@/lib/scope-switcher"
import { cn } from "@/lib/utils"
import { isScopeFixed } from "@/lib/workspace-role"

export interface ScopeSwitcherBag {
  config: ScopeConfig
  /**
   * `"open"` means both names below are set. `"choose"` means this product is
   * licensed for scopes but the session has not picked one here, so every trigger
   * reads the prompt and the menu asks. `"none"` means nothing is licensed.
   */
  status: ScopeStatus
  parent: ScopeParent | null
  child: ScopeChild | null
  /** Trigger text: the child's name, or the prompt when there is nothing to name. */
  label: string
  /** Parent's name, or the product-level prompt. Never empty. */
  parentLabel: string
  suggestedParent: ScopeParent | null
  selectScope: (parent: ScopeParent, child: ScopeChild) => void
  subView: "main" | "parents"
  /** Pass to `DropdownMenu` so the menu reopens on the main view. */
  onOpenChange: (open: boolean) => void
  showParents: () => void
  showMain: () => void
  selectParent: (parent: ScopeParent) => void
  selectChild: (child: ScopeChild) => void
  /**
   * True when this session does not choose its scope (`isScopeFixed`), so every
   * trigger renders the two names without a menu behind them. Decided here rather
   * than at each trigger: the three surfaces already share this bag, and a
   * predicate copied three times is a predicate one surface forgets.
   */
  fixed: boolean
  /** `"<Parent> · <Child>. <how to change it, or what it is>"` — for `aria-label`. */
  ariaLabel: string
  /** Super-admin home: workspace is institute-wide, not one program. */
  allPrograms?: boolean
  /** Whether this session may choose institute-wide scope. */
  canSelectAllPrograms?: boolean
  /** Select institute-wide (all programs) scope. */
  selectAllPrograms?: () => void
  /** Home demo: program list is only institute-wide; school may still change. */
  allProgramsScopeOnly?: boolean
  /** Whether this session may switch schools (tenet admin, Faculty + Admin). */
  canChangeSchool: boolean
}

function SchoolScopeHeader({
  school,
  showChange = false,
}: {
  school: ScopeParent
  showChange?: boolean
}) {
  return (
    <>
      <Avatar className="size-9 shrink-0">
        <AvatarImage
          src={school.logo}
          alt=""
          referrerPolicy="origin"
          className="object-contain p-0.5"
        />
        <AvatarFallback className="text-xs font-semibold">{school.initials}</AvatarFallback>
      </Avatar>
      <p className="min-w-0 flex-1 break-words text-sm font-semibold leading-snug whitespace-normal">
        {school.name}
      </p>
      {showChange ? (
        <span className="shrink-0 text-sm font-medium text-brand">Change</span>
      ) : null}
    </>
  )
}

/** Shared state + handlers for any trigger that opens `ScopeSwitcherMenuBody`. */
export function useScopeSwitcher(product: Product, customIndex?: number): ScopeSwitcherBag {
  const {
    config: rawConfig,
    status,
    parent,
    child,
    suggestedParent,
    selectParent,
    selectChild,
    selectScope,
  } = useActiveScope(product, customIndex)
  const session = useLoginSession()
  const sessionScopeKey = [
    session.demoFlowId ?? "",
    session.role,
    session.administeredProgramIds?.join(",") ?? "*",
    session.programPersonas?.map(grant => grant.programId).join(",") ?? "",
  ].join(":")
  const config = React.useMemo(
    () => resolveScopeConfigForSession(rawConfig),
    [rawConfig, sessionScopeKey],
  )
  const [subView, setSubView] = React.useState<"main" | "parents">("main")
  const fixed = isScopeFixed()
  const canChangeSchool = sessionMayChangeSchool() && config.parents.length > 1

  const named = parent !== null && child !== null

  return {
    config,
    status,
    parent,
    child,
    label: child?.name ?? config.choosePrompt,
    parentLabel: parent?.name ?? config.choosePrompt,
    suggestedParent,
    selectScope,
    subView,
    onOpenChange: open => {
      if (!open) setSubView("main")
    },
    showParents: () => setSubView("parents"),
    showMain: () => setSubView("main"),
    selectParent: next => {
      selectParent(next)
      setSubView("main")
    },
    selectChild,
    fixed,
    canChangeSchool,
    // Unresolved has no two names to read out, so the label carries the ask
    // instead. Keeping the same slot means no trigger needs its own branch.
    ariaLabel: named
      ? `${parent.name} · ${child.name}. ${fixed ? config.fixedAriaSuffix : config.ariaSuffix}`
      : config.choosePrompt,
  }
}

/** Select a program by child id through an existing scope switcher bag. */
export function selectProgramByChildId(
  scope: Pick<ScopeSwitcherBag, "config" | "selectScope">,
  programId: string,
): boolean {
  for (const parent of scope.config.parents) {
    const child = scope.config.childrenOf(parent).find(entry => entry.id === programId)
    if (child) {
      scope.selectScope(parent, child)
      return true
    }
  }
  return false
}

function programListLabel(childNoun: string): string {
  return childNoun === "Program" ? "Recently selected" : childNoun
}

function programsForMenu(
  parent: ScopeParent,
  children: ReadonlyArray<ScopeChild>,
): ScopeChild[] {
  const rank = new Map(recentProgramIds(parent.id).map((id, index) => [id, index]))
  return [...children].sort((a, b) => {
    const left = rank.get(a.id) ?? Number.MAX_SAFE_INTEGER
    const right = rank.get(b.id) ?? Number.MAX_SAFE_INTEGER
    return left - right
  })
}

function matchesScopeQuery(name: string, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return name.toLowerCase().includes(needle)
}

/** Search sits in the menu once the list is past the shared filter threshold. */
function ScopeListSearch({
  value,
  onChange,
  noun,
}: {
  value: string
  onChange: (value: string) => void
  noun: string
}) {
  return (
    <div className="px-2 pb-1.5" onKeyDown={event => event.stopPropagation()}>
      <div className="relative">
        <i
          className="fa-light fa-magnifying-glass pointer-events-none absolute start-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          placeholder="Search…"
          value={value}
          onChange={event => onChange(event.target.value)}
          onKeyDown={event => event.stopPropagation()}
          className={cn("h-8 ps-7 text-xs", value ? "pe-8" : "pe-2")}
          autoFocus
          aria-label={`Search ${noun.toLowerCase()}s`}
        />
        {value ? (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onChange("")}
            className="icon-button-chrome absolute end-1.5 top-1/2 inline-flex size-8 -translate-y-1/2 items-center justify-center rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
          >
            <i className="fa-light fa-xmark text-xs" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  )
}

/** Menu contents. Render inside a `DropdownMenuContent`. */
export function ScopeSwitcherMenuBody({ scope }: { scope: ScopeSwitcherBag }) {
  const { config, parent, child, subView, showParents, showMain } = scope
  const school = sessionMenuParent(config, parent?.id)
  const [programQuery, setProgramQuery] = React.useState("")

  React.useEffect(() => {
    setProgramQuery("")
  }, [school.id, subView])

  const programs = programsForMenu(school, config.childrenOf(school))
  const visiblePrograms = programs.filter(option => matchesScopeQuery(option.name, programQuery))

  // Nothing chosen here yet: the same question the chooser page asks, in menu
  // items, so the chrome is a second way to answer it rather than dead until
  // the page is used. Every row names both halves because there is no current
  // school to hang a bare program on.
  if ((parent === null || child === null) && !scope.allPrograms) {
    if (scope.status === "none") {
      return (
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {`No ${config.childNoun.toLowerCase()}s for your account`}
        </DropdownMenuLabel>
      )
    }

    const grouped = config.parents.map(option => ({
      option,
      children: programsForMenu(option, config.childrenOf(option)).filter(childOption =>
        matchesScopeQuery(childOption.name, programQuery),
      ),
    }))
    const groupedCount = config.parents.reduce(
      (count, option) => count + config.childrenOf(option).length,
      0,
    )

    return (
      <>
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {programListLabel(config.childNoun)}
        </DropdownMenuLabel>
        {groupedCount > FILTER_LIST_SEARCH_THRESHOLD ? (
          <ScopeListSearch
            value={programQuery}
            onChange={setProgramQuery}
            noun={config.childNoun}
          />
        ) : null}
        {grouped.every(group => group.children.length === 0) ? (
          <p className="px-3 py-2 text-xs text-muted-foreground">No matches.</p>
        ) : (
          grouped.map(group =>
            group.children.length === 0 ? null : (
              <React.Fragment key={group.option.id}>
                {config.parents.length > 1 ? (
                  <DropdownMenuLabel className="pt-2 text-xs font-normal text-muted-foreground">
                    {group.option.name}
                  </DropdownMenuLabel>
                ) : null}
                {group.children.map(childOption => (
                  <DropdownMenuItem
                    key={`${group.option.id}-${childOption.id}`}
                    onClick={() => scope.selectScope(group.option, childOption)}
                    className="items-start py-2"
                  >
                    <i
                      className={`fa-light fa-${config.childIcon} mt-0.5 shrink-0 text-xs`}
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 break-words whitespace-normal">
                      {childOption.name}
                    </span>
                  </DropdownMenuItem>
                ))}
              </React.Fragment>
            ),
          )
        )}
      </>
    )
  }

  if (subView === "parents" && scope.canChangeSchool) {
    return (
      <>
        <DropdownMenuItem
          onSelect={e => {
            e.preventDefault()
            showMain()
          }}
        >
          <i className="fa-light fa-arrow-left text-xs" aria-hidden="true" />
          <span>Back</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {config.parentSelectLabel}
        </DropdownMenuLabel>
        {config.parents.map(option => (
          <DropdownMenuItem
            key={option.id}
            // Stay open and return to the program/site list — closing here
            // forced a second open just to finish the scope pick.
            onSelect={event => {
              event.preventDefault()
              scope.selectParent(option)
            }}
            className="items-start py-2"
          >
            <Avatar size="sm" className="mt-0.5 shrink-0">
              <AvatarImage src={option.logo} alt="" referrerPolicy="origin" />
              <AvatarFallback className="text-xs font-semibold">
                {option.initials}
              </AvatarFallback>
            </Avatar>
            <span className="min-w-0 flex-1 break-words whitespace-normal">
              {option.name}
            </span>
            {parent && option.id === parent.id && (
              <i
                className="fa-solid fa-check ms-1 mt-0.5 shrink-0 text-xs text-brand"
                aria-hidden="true"
              />
            )}
          </DropdownMenuItem>
        ))}
      </>
    )
  }

  if (scope.allProgramsScopeOnly) {
    return (
      <>
        <div className="p-1">
          {scope.canChangeSchool ? (
            <button
              type="button"
              onClick={showParents}
              className={cn(
                "flex w-full items-center gap-2.5 rounded-md p-2 text-left transition-colors",
                "hover:bg-interactive-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
              )}
            >
              <SchoolScopeHeader school={school} showChange />
            </button>
          ) : (
            <div className="flex w-full items-center gap-2.5 rounded-md p-2">
              <SchoolScopeHeader school={school} />
            </div>
          )}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          {programListLabel(config.childNoun)}
        </DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => scope.selectAllPrograms?.()}
          className="items-start py-2"
        >
          <i
            className={`fa-light fa-${config.childIcon} mt-0.5 shrink-0 text-xs`}
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1 break-words whitespace-normal">All programs</span>
          <i
            className="fa-solid fa-check ms-1 mt-0.5 shrink-0 text-xs text-brand"
            aria-hidden="true"
          />
        </DropdownMenuItem>
      </>
    )
  }

  return (
    <>
      <div className="p-1">
        {scope.canChangeSchool ? (
          <button
            type="button"
            onClick={showParents}
            className={cn(
              "flex w-full items-center gap-2.5 rounded-md p-2 text-left transition-colors",
              "hover:bg-interactive-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
            )}
          >
            <SchoolScopeHeader school={school} showChange />
          </button>
        ) : (
          <div className="flex w-full items-center gap-2.5 rounded-md p-2">
            <SchoolScopeHeader school={school} />
          </div>
        )}
      </div>

      <DropdownMenuSeparator />

      <DropdownMenuLabel className="text-xs text-muted-foreground">
        {programListLabel(config.childNoun)}
      </DropdownMenuLabel>
      {programs.length > FILTER_LIST_SEARCH_THRESHOLD ? (
        <ScopeListSearch
          value={programQuery}
          onChange={setProgramQuery}
          noun={config.childNoun}
        />
      ) : null}
      <div className="max-h-72 overflow-y-auto">
      {visiblePrograms.length === 0 ? (
        <p className="px-3 py-2 text-xs text-muted-foreground">No matches.</p>
      ) : (
      visiblePrograms.map(option => (
        <DropdownMenuItem
          key={option.id}
          onClick={() => scope.selectScope(school, option)}
          className="items-start py-2"
        >
          <i
            className={`fa-light fa-${config.childIcon} mt-0.5 shrink-0 text-xs`}
            aria-hidden="true"
          />
          <span className="min-w-0 flex-1 break-words whitespace-normal">
            {option.name}
          </span>
          {child && option.id === child.id && (
            <i
              className="fa-solid fa-check ms-1 mt-0.5 shrink-0 text-xs text-brand"
              aria-hidden="true"
            />
          )}
        </DropdownMenuItem>
      ))
      )}
      </div>
    </>
  )
}

const PANEL_ROW =
  "flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-interactive-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"

/**
 * The school selector, as a blocking panel — same IA as {@link ScopeSwitcherMenuBody}
 * (school header + Change, then program rows with glyphs), without a dropdown.
 *
 * Used when a product switch cannot carry the program you left: the dialog owns
 * the question, so nesting the utility-bar menu under its overlay would open
 * behind it. Same presses, same icons, in the dialog's own surface.
 */
export function ScopeSwitcherPanel({
  config: rawConfig,
  suggestedParentId,
  onPick,
  className,
}: {
  config: ScopeConfig
  suggestedParentId?: string | null
  onPick: (parent: ScopeParent, child: ScopeChild) => void
  className?: string
}) {
  const config = React.useMemo(() => resolveScopeConfigForSession(rawConfig), [rawConfig])
  const canChangeSchool =
    sessionMayChangeSchool() && config.parents.length > 1

  const initialParent =
    (suggestedParentId
      ? config.parents.find(p => p.id === suggestedParentId)
      : null) ?? config.parents[0]

  const [view, setView] = React.useState<"main" | "parents">("main")
  const [parent, setParent] = React.useState<ScopeParent | null>(initialParent ?? null)
  const [programQuery, setProgramQuery] = React.useState("")

  if (!parent || config.parents.length === 0) {
    return (
      <p className="px-2 py-3 text-sm text-muted-foreground">
        {`No ${config.childNoun.toLowerCase()}s for your account`}
      </p>
    )
  }

  const programs = config.childrenOf(parent)
  const visiblePrograms = programsForMenu(parent, programs).filter(option =>
    matchesScopeQuery(option.name, programQuery),
  )

  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xs ring-1 ring-foreground/10",
        className,
      )}
    >
      {view === "parents" && canChangeSchool ? (
        <div className="flex flex-col p-1">
          <button type="button" onClick={() => setView("main")} className={PANEL_ROW}>
            <i className="fa-light fa-arrow-left mt-0.5 shrink-0 text-xs" aria-hidden="true" />
            <span>Back</span>
          </button>
          <div className="my-1 h-px bg-border" role="separator" />
          <p className="px-2 py-1.5 text-xs text-muted-foreground">{config.parentSelectLabel}</p>
          {config.parents.map(option => (
            <button
              key={option.id}
              type="button"
              onClick={() => {
                setParent(option)
                setProgramQuery("")
                setView("main")
              }}
              className={PANEL_ROW}
            >
              <Avatar size="sm" className="mt-0.5 shrink-0">
                <AvatarImage src={option.logo} alt="" referrerPolicy="origin" />
                <AvatarFallback className="text-xs font-semibold">
                  {option.initials}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0 flex-1 break-words whitespace-normal">
                {option.name}
              </span>
              {option.id === parent.id ? (
                <i
                  className="fa-solid fa-check ms-1 mt-0.5 shrink-0 text-xs text-brand"
                  aria-hidden="true"
                />
              ) : null}
            </button>
          ))}
        </div>
      ) : (
        <div className="flex flex-col">
          <div className="p-1">
            {canChangeSchool ? (
              <button
                type="button"
                onClick={() => setView("parents")}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-md p-2 text-left transition-colors",
                  "hover:bg-interactive-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                )}
              >
                <SchoolScopeHeader school={parent} showChange />
              </button>
            ) : (
              <div className="flex w-full items-center gap-2.5 rounded-md p-2">
                <SchoolScopeHeader school={parent} />
              </div>
            )}
          </div>

          <div className="h-px bg-border" role="separator" />

          <div className="flex flex-col p-1">
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              {programListLabel(config.childNoun)}
            </p>
            {programs.length > FILTER_LIST_SEARCH_THRESHOLD ? (
              <ScopeListSearch
                value={programQuery}
                onChange={setProgramQuery}
                noun={config.childNoun}
              />
            ) : null}
            <div className="max-h-72 overflow-y-auto">
            {visiblePrograms.length === 0 ? (
              <p className="px-2 py-2 text-xs text-muted-foreground">No matches.</p>
            ) : (
            visiblePrograms.map(option => (
              <button
                key={option.id}
                type="button"
                onClick={() => onPick(parent, option)}
                className={PANEL_ROW}
              >
                <i
                  className={`fa-light fa-${config.childIcon} mt-0.5 shrink-0 text-xs`}
                  aria-hidden="true"
                />
                <span className="min-w-0 flex-1 break-words whitespace-normal">
                  {option.name}
                </span>
              </button>
            ))
            )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
